import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GuardsCheckEnd, NavigationEnd, Router } from '@angular/router';
import { AnalyticsService } from '@services/analytics.service';
import { AppService } from '@services/app.service';
import { AuthService } from '@services/auth.service';
import { AuthStorage } from '@services/auth.storage';
import { DemoPharmaciesService } from '@services/demo-pharmacies.service';
import { FeatureFlagsService } from '@services/feature-flags.service';
import { ThemeService } from '@services/theme.service';
import { UserService } from '@services/user.service';
import { EMPTY, from, timer } from 'rxjs';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: 'app.main.html',
  standalone: false,
})
export class AppComponent {
  private isActiveWindow = true;
  private service = inject(AppService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private userService = inject(UserService);
  private analyticsService = inject(AnalyticsService);
  private featureFlagsService = inject(FeatureFlagsService);
  private themeService = inject(ThemeService);
  private demoPharmaciesService = inject(DemoPharmaciesService);
  private isFirstAuthEmission = true;
  private authEmissionVersion = 0;

  constructor() {
    this.trackAuthStatus();
    this.trackPageChange();
    this.trackWindowState();
    this.trackLocalStorage();
    this.trackStores();
    this.trackFeatureFlags();
  }

  private trackWindowState(): void {
    window.addEventListener('blur', () => {
      this.isActiveWindow = false;
    });
    window.addEventListener('focus', () => {
      this.isActiveWindow = true;
    });
  }

  private trackLocalStorage(): void {
    window.addEventListener('storage', event => {
      if (event.storageArea === localStorage) {
        const logout = localStorage.getItem(AuthStorage.LOGOUT_INITIATED);
        if (!!logout && !this.isActiveWindow) {
          this.router.navigate(['/login']);
        }
      }
    });
  }

  private trackPageChange(): void {
    this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.analyticsService.track('route', { url: event.url });
      } else if (event instanceof GuardsCheckEnd) {
        if (!event.shouldActivate) {
          this.authService.logout('AuthGuardCheck');
        }
      }
    });
  }

  private trackAuthStatus(): void {
    this.authService.authStatusChanged.pipe(takeUntilDestroyed(), distinctUntilChanged()).subscribe(authenticated => {
      const skipReload = this.isFirstAuthEmission;
      this.isFirstAuthEmission = false;
      const authEmissionVersion = ++this.authEmissionVersion;
      if (authenticated) {
        void this.handleAuthenticated(skipReload, authEmissionVersion);
      } else {
        this.service.clearStores();
        if (!skipReload) void this.featureFlagsService.reload();
      }
    });
  }

  private async handleAuthenticated(skipReload: boolean, authEmissionVersion: number): Promise<void> {
    if (!skipReload) await this.featureFlagsService.reload();
    if (authEmissionVersion !== this.authEmissionVersion || !this.authService.authStatus()) return;
    const user = UserService.getCurrent();
    if (!this.userService.hasAcceptedAgreements(user)) {
      this.router.navigate(['/agreement']);
    } else {
      this.authService.redirectToStartingPage();
    }
  }

  private trackStores() {
    const fiveMinutes = 5 * 60 * 1000;
    timer(fiveMinutes, fiveMinutes)
      .pipe(
        switchMap(() => {
          if (!this.authService.isUserAuthenticated()) return EMPTY;
          return from(this.service.refreshStores());
        }),
      )
      .subscribe(() => console.debug('Stores refresh done'));
  }

  private trackFeatureFlags() {
    const fiveMinutes = 5 * 60 * 1000;
    timer(fiveMinutes, fiveMinutes)
      .pipe(
        switchMap(() => {
          if (!this.authService.isUserAuthenticated()) return EMPTY;
          return from(this.featureFlagsService.reload());
        }),
      )
      .subscribe(() => console.debug('Feature flags refresh done'));
  }
}
